import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, IconButton, Paper } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import {
    GoogleMap,
    DrawingManager,
    Circle,
    Rectangle,
    Polygon,
} from "@react-google-maps/api";
import { useGoogleMaps } from 'config/GoogleMapsProvider'

const IRELAND_CENTER = { lat: 53.4, lng: -7.9 }; // nice middle

const containerStyle = {
    width: "100%",
    height: "650px",
};

// Dedicated zoom controls component
const ZoomControls = ({ onZoomIn, onZoomOut }) => {
    return (
        <Paper
            elevation={3}
            style={{
                position: 'absolute',
                bottom: 16, // bottom right position (pan controls hidden)
                right: 16,
                padding: '10px 12px',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12
            }}
        >
            <IconButton
                aria-label="Zoom in"
                onClick={onZoomIn}
                style={{ width: 56, height: 56 }}
            >
                <AddIcon style={{ fontSize: 40 }} />
            </IconButton>
            <IconButton
                aria-label="Zoom out"
                onClick={onZoomOut}
                style={{ width: 56, height: 56 }}
            >
                <RemoveIcon style={{ fontSize: 40 }} />
            </IconButton>
        </Paper>
    )
}

export default function CoverageMap({ onShapesChange, areas, updateAreas }) {
    const [shapes, setShapes] = useState(areas || []); // canonical list
    const [circleInstances, setCircleInstances] = useState({}) // map of shapeId -> google.maps.Circle
    const [drawingMode, setDrawingMode] = useState(null) // null | 'circle'
    const mapRef = useRef(null)

    // Backwards compatibility: if consumer passes updateAreas instead of onShapesChange
    const emitChange = (next) => {
        if (onShapesChange) onShapesChange(next)
        if (updateAreas) updateAreas(next)
    }

    const { isLoaded, loadError } = useGoogleMaps()

    // Sync external areas prop into internal state if provided
    useEffect(() => {
        if (areas && Array.isArray(areas)) {
            setShapes(areas)
        }
    }, [areas])

    const onOverlayComplete = useCallback(
        (e) => {
            const overlay = e.overlay;
            const type = e.type; // 'circle' | 'polygon' | 'rectangle' | ...

            // IMPORTANT: we should take the data we need, then remove the uncontrolled overlay
            overlay.setMap(null);

            let shapePayload = null;

            const id = Date.now() + '-' + Math.random().toString(36).slice(2, 8)
            if (type === "circle") {
                const center = overlay.getCenter().toJSON();
                const radius = overlay.getRadius(); // in meters
                shapePayload = {
                    id,
                    type: "circle",
                    center,
                    radius,
                };
            } else if (type === "polygon") {
                const path = overlay.getPath().getArray().map((latLng) => latLng.toJSON());
                shapePayload = {
                    id,
                    type: "polygon",
                    path,
                };
            } else if (type === "rectangle") {
                const bounds = overlay.getBounds();
                const ne = bounds.getNorthEast().toJSON();
                const sw = bounds.getSouthWest().toJSON();
                shapePayload = {
                    id,
                    type: "rectangle",
                    bounds: {
                        north: ne.lat,
                        east: ne.lng,
                        south: sw.lat,
                        west: sw.lng,
                    },
                };
            }

            if (shapePayload) {
                setShapes((prev) => {
                    const next = [...prev, shapePayload];
                    // bubble up to parent so you can POST to backend
                    emitChange(next);
                    return next;
                });
            }
            // After placing one circle, revert to hand tool
            setDrawingMode(null)
        },
        [emitChange]
    );

    // Static style objects reused both for DrawingManager and persisted shapes
    const shapeStyles = {
        circle: {
            fillColor: "#229626", // site green
            fillOpacity: 0.15,
            strokeWeight: 2,
            strokeColor: "#229626",
            clickable: false,
            editable: false,
            zIndex: 1,
        },
        polygon: {
            fillColor: "#4285F4",
            fillOpacity: 0.15,
            strokeWeight: 2,
            strokeColor: "#4285F4",
            clickable: false,
            editable: false,
            zIndex: 1,
        },
        rectangle: {
            fillColor: "#4285F4",
            fillOpacity: 0.15,
            strokeWeight: 2,
            strokeColor: "#4285F4",
            clickable: false,
            editable: false,
            zIndex: 1,
        }
    }

    // Only build drawing options after API is loaded to avoid accessing undefined google internals
    const drawingOptions = useMemo(() => {
        if (!isLoaded || !window.google?.maps) return undefined
        return {
            drawingMode: drawingMode, // programmatic
            drawingControl: false, // hide default toolbar
            circleOptions: shapeStyles.circle,
            polygonOptions: shapeStyles.polygon,
            rectangleOptions: shapeStyles.rectangle,
        }
    }, [isLoaded, drawingMode, shapeStyles.circle, shapeStyles.polygon, shapeStyles.rectangle])

    const handleClear = () => {
        setShapes([]);
        emitChange([]);
    };

    const handleRemove = (id) => {
        setShapes(prev => {
            const next = prev.filter(s => s.id !== id)
            emitChange(next)
            return next
        })
        setCircleInstances(prev => {
            const copy = { ...prev }
            if (copy[id]) {
                copy[id].setMap(null)
                delete copy[id]
            }
            return copy
        })
    }


    // Commit new center when drag ends (single update)
    const commitCenterChange = (id) => {
        const circle = circleInstances[id]
        if (!circle) return
        setShapes(prev => {
            const idx = prev.findIndex(s => s.id === id)
            if (idx === -1) return prev
            const center = circle.getCenter().toJSON()
            if (prev[idx].center.lat === center.lat && prev[idx].center.lng === center.lng) return prev
            const updated = { ...prev[idx], center }
            const next = [...prev]
            next[idx] = updated
            emitChange(next)
            return next
        })
    }


    if (loadError) return <div>Map failed to load</div>;
    if (!isLoaded) return <div>Loading…</div>;

    // Some builds report invariant violation if drawing lib missing; add guard
    const drawingAvailable = isLoaded && !!(window.google && window.google.maps && window.google.maps.drawing)
    if (!drawingAvailable) {
        // Fallback message with guidance
        return (
            <div style={{ padding: 12, background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 4 }}>
                <strong>Map loaded but drawing tools unavailable.</strong><br />
                Ensure your Google Maps script includes the drawing library (&libraries=drawing,places) and that your API key allows the Maps JavaScript API. Current libraries param requested via useJsApiLoader: drawing, places.
            </div>
        )
    }

    return (
        <div style={{ position: "relative" }}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={IRELAND_CENTER}
                zoom={7}
                options={{
                    disableDefaultUI: true, // hide native controls entirely
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    zoomControl: false,
                }}
                onLoad={(map) => { mapRef.current = map }}
            >
                {/* Drawing manager to capture new shapes */}
                {drawingOptions && (
                    <DrawingManager
                        onOverlayComplete={onOverlayComplete}
                        options={drawingOptions}
                    />
                )}

                {/* Persisted shapes */}
                {shapes.map(shape => {
                    if (shape.type === 'circle') {
                        return (
                            <Circle
                                key={shape.id}
                                center={shape.center}
                                radius={shape.radius}
                                options={{ ...shapeStyles.circle, clickable: true }}
                                draggable
                                onLoad={(circle) => setCircleInstances(prev => ({ ...prev, [shape.id]: circle }))}
                                onDragEnd={() => commitCenterChange(shape.id)}
                                onClick={(e) => {
                                    if (e.domEvent && e.domEvent.shiftKey) { // allow shift+click to delete
                                        handleRemove(shape.id)
                                    }
                                }}
                            />
                        )
                    }
                    if (shape.type === 'rectangle') {
                        return (
                            <Rectangle
                                key={shape.id}
                                bounds={shape.bounds}
                                options={{ ...shapeStyles.rectangle, clickable: true }}
                                onClick={() => handleRemove(shape.id)}
                            />
                        )
                    }
                    if (shape.type === 'polygon') {
                        return (
                            <Polygon
                                key={shape.id}
                                path={shape.path}
                                options={{ ...shapeStyles.polygon, clickable: true }}
                                onClick={() => handleRemove(shape.id)}
                            />
                        )
                    }
                    return null
                })}
            </GoogleMap>

            {/* info + actions overlay */}
            <div
                style={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    background: "white",
                    padding: "8px 12px",
                    borderRadius: 6,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                    maxWidth: 280
                }}
            >
                <div style={{ marginBottom: 12 }}>
                    <span style={{ color: '#229626', fontWeight: 600 }}>{shapes.length} area{shapes.length === 1 ? "" : "s"} selected</span>
                </div>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setDrawingMode('circle')}
                    disabled={drawingMode === 'circle'}
                    style={{
                        backgroundColor: '#229626',
                        color: '#fff',
                        fontWeight: 600,
                        padding: '10px 16px'
                    }}
                >{drawingMode === 'circle' ? 'Click & drag on map…' : 'Add Circle'}</Button>
                <Button
                    onClick={handleClear}
                    size="small"
                    style={{
                        color: '#f5365c',
                        marginTop: 8,
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >Clear areas</Button>
                <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>Drag circles to move. Shift+click to remove.</div>
            </div>
            {/* Zoom controls bottom-right only */}
            <ZoomControls
                onZoomIn={() => { const z = mapRef.current?.getZoom(); if (z != null) mapRef.current.setZoom(z + 1) }}
                onZoomOut={() => { const z = mapRef.current?.getZoom(); if (z != null) mapRef.current.setZoom(z - 1) }}
            />
        </div>
    );
}